// 1:1 port of app/index.html — Mission Control dashboard.
// Period selector (1D/7D/30D/MTD + Custom range) + 5 status cards + merged
// anomaly feed (tenant-anomalies + anomalies) grouped by category + promo banner.
//
// Tenant dispatch: ConsumerAffairs has a different daily operating shape
// (lead-gen, no purchases, form_score↔VMD drift) so it gets its own
// Mission Control implementation (IndexCA.tsx). Route through to that
// before any of the SP/4P-shaped queries fire.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../lib/api'
import { useAuth } from '../lib/auth'
import { IndexCA } from './IndexCA'
import { IndexWarmies } from './IndexWarmies'
import './Index.css'

// ─────────────────── Types ───────────────────

// Matches the actual /api/status response (see app/api.py:1712).
interface StatusCard {
  label?: string
  value: number | string
  subtitle?: string
  status?: 'green' | 'yellow' | 'red' | string
  target?: number
  display?: string      // pre-formatted string (margin card)
  type?: string         // 'currency' | 'percentage' (margin card)
  change_pct?: number   // leads card
}
interface AnomalyCounts {
  critical?: number; high?: number; medium?: number; total?: number
}
interface PromoActive {
  name?: string; start_date?: string; end_date?: string; tier?: string
}
interface StatusResp {
  status: string
  message?: string
  code?: string
  cards?: { mer?: StatusCard; cac?: StatusCard; margin?: StatusCard; leads?: StatusCard; kpi5?: StatusCard }
  anomalies?: AnomalyCounts
  promo?: PromoActive | null
  data_month?: string
  data_source?: string
}

// Unified shape covering both /api/anomalies and /api/tenant-anomalies rows.
// /api/tenant-anomalies returns: id, tier, category, title, description, daily_impact, action, date, lineage_available
// /api/anomalies returns: anomaly_id, anomaly_date, category, title, description, channel, tier
interface AnomalyRow {
  // Common
  category?: string
  title?: string
  description?: string
  channel?: string
  tier?: string
  // From /api/anomalies
  anomaly_id?: string
  anomaly_date?: string
  // From /api/tenant-anomalies
  id?: string
  date?: string
  daily_impact?: number
  action?: string
  lineage_available?: boolean
  // Internal grouping
  _idx?: number
}
interface AnomaliesListResp {
  status: string
  anomalies?: AnomalyRow[]
  anomaly_date?: string
  detected_at?: string
}
interface TenantAnomaliesResp {
  status: string
  anomalies?: AnomalyRow[]
  date?: string
  lineage_diagrams?: Record<string, unknown>
}

// ─────────────────── Helpers ───────────────────

// Map server status colors → CSS class on .status-card (warning/critical).
function cardClass(s: string | undefined): string {
  if (s === 'red') return 'critical'
  if (s === 'yellow') return 'warning'
  return ''  // green / ok → no extra class
}
function cardValue(c?: StatusCard): string {
  if (!c) return '—'
  if (c.display) return c.display       // pre-formatted (e.g. "$2.4M")
  return String(c.value ?? '—')
}

function tierClass(t: string | undefined) {
  const v = (t || '').toLowerCase()
  if (v === 'critical') return 'critical'
  if (v === 'high' || v === 'warning') return 'high'
  if (v === 'positive') return 'positive'
  return 'medium'
}

// Date-only safe parser (avoids UTC off-by-one for YYYY-MM-DD strings).
// Ported from app/js/date-utils.js.
function parseDateSafe(input?: string | null): Date | null {
  if (!input) return null
  let d: Date
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    d = new Date(input + 'T12:00:00')
  } else {
    d = new Date(input)
  }
  return isNaN(d.getTime()) ? null : d
}

function formatDateBadge(input?: string | null): string {
  const d = parseDateSafe(input)
  if (!d) return ''
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// Returns one of: 'fresh' | 'recent' | 'stale' | 'very-stale' | 'unknown'.
// Drives badge color: fresh/recent = green, stale = yellow, very-stale = red.
function getFreshness(input?: string | null): string {
  const d = parseDateSafe(input)
  if (!d) return 'unknown'
  const now = new Date()
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diff = Math.floor((nowOnly.getTime() - dateOnly.getTime()) / 86400000)
  if (diff === 0) return 'fresh'
  if (diff === 1) return 'recent'
  if (diff <= 7) return 'stale'
  return 'very-stale'
}

// Anomaly category groups + display order. Mirrors app/index.html:730-772.
// Section labels are intentionally text-only (no emoji per user preference).
const CATEGORY_GROUPS: Record<string, { label: string; categories: string[] }> = {
  opportunity: { label: 'Opportunities', categories: [
    'performance_surge', 'revenue_surge', 'mer_improvement', 'aov_increase',
    'purchase_volume_surge', 'new_customer_surge', 'cpl_improvement',
  ]},
  business: { label: 'Core Business', categories: [
    'revenue_drop', 'revenue_decline_sustained', 'mer_decline', 'aov_drop',
    'purchase_volume_drop', 'new_customer_drop', 'margin_compression', 'efficiency',
  ]},
  channel: { label: 'Channel Performance', categories: [
    'channel_cpc_spike', 'channel_cpm_spike', 'channel_cvr_drop', 'channel_cvr_decline_sustained',
    'channel_revenue_drop', 'channel_conversion_drop', 'channel_roas_drop',
    'channel_leads_drop', 'channel_leads_decline_sustained',
    'cpc_cpm_spike', 'cpc_spike', 'cpm_spike',
  ]},
  funnel: { label: 'Funnel & Leads', categories: [
    'cvr_drop', 'funnel_collapse', 'aggregate_leads_drop', 'funnel_lead_rate_drop',
    'tracking_failure', 'conversion_volume_drop', 'data_pipeline_failure',
  ]},
  creative: { label: 'Creative Health', categories: [
    'creative_fatigue', 'creative_health_declining',
  ]},
  other: { label: 'Other Signals', categories: [] },
}
const GROUP_RENDER_ORDER: Array<keyof typeof CATEGORY_GROUPS> =
  ['opportunity', 'business', 'channel', 'funnel', 'other', 'creative']

// Categories whose daily_impact reflects money LOST (must render as negative).
// Mirrors isNegativeCategory in app/index.html:780-790.
const NEGATIVE_IMPACT_CATEGORIES = new Set([
  'gradual_roas_decline', 'roas_collapse', 'cvr_drop', 'ctr_drop',
  'funnel_collapse', 'cpc_spike', 'cpm_spike', 'tracking_failure', 'data_pipeline_failure',
  'creative_health_declining', 'creative_fatigue', 'iroas_drift_high', 'confidence_decay',
  'channel_cpc_spike', 'channel_cpm_spike', 'channel_cvr_drop', 'channel_cvr_decline_sustained',
  'channel_revenue_drop', 'channel_conversion_drop', 'channel_roas_drop',
  'channel_leads_drop', 'channel_leads_decline_sustained', 'aggregate_leads_drop', 'funnel_lead_rate_drop',
])
// Categories hidden on Mission Control (shown on ghost.html / experiments page).
const HIDDEN_CATEGORIES = new Set([
  'experiment_stale', 'spend_beyond_experiment', 'iroas_drift_high', 'confidence_decay',
])
// "At risk" categories — show "at risk" suffix instead of $ impact direction.
const AT_RISK_CATEGORIES = new Set(['experiment_stale', 'spend_beyond_experiment'])

function bucketize(anomalies: AnomalyRow[]): Record<string, AnomalyRow[]> {
  const out: Record<string, AnomalyRow[]> = {
    opportunity: [], business: [], channel: [], funnel: [], creative: [], other: [],
  }
  ;(anomalies || []).forEach((a, idx) => {
    const stamped = { ...a, _idx: idx }
    let placed = false
    for (const [group, cfg] of Object.entries(CATEGORY_GROUPS)) {
      if (group === 'other') continue
      if (cfg.categories.includes(a.category || '')) {
        out[group].push(stamped); placed = true; break
      }
    }
    if (!placed) out.other.push(stamped)
  })
  return out
}

// ─────────────────── Period control ───────────────────

const PERIODS: { label: string; days: number }[] = [
  { label: '1D',  days: 1 },
  { label: '7D',  days: 7 },
  { label: '30D', days: 30 },
  { label: 'MTD', days: -1 },  // -1 sentinel for month-to-date (server handles)
]

// ─────────────────── Component ───────────────────

export function Index() {
  // Tenant dispatch — CA and Warmies keep their own Mission Control.
  const { me, activeTenant } = useAuth()
  const tenant = activeTenant || me?.tenant_id
  // Horizon rolled back as the home page (2026-10-12): every tenant gets its
  // classic Mission Control again. Horizon stays reachable at /horizon.
  if (tenant === 'consumeraffairs') return <IndexCA />
  if (tenant === 'warmies') return <IndexWarmies />
  return <MissionControlClassic />
}

// Mission Control classic — the KPI + anomaly home page for SP, 4P and any
// tenant without its own. Also reachable at /mission-control-classic.
export function MissionControlClassic() {
  // Period state: either a preset (days >= 1 or -1 for MTD) or a custom range.
  const [period, setPeriod] = useState<number>(30)
  const [compareMode, setCompareMode] = useState<'previous' | 'yoy'>('previous')

  // Custom date range — when set, status query uses ?start=&end= instead of ?days=.
  const [customRange, setCustomRange] = useState<{ start: string; end: string } | null>(null)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [pendingStart, setPendingStart] = useState('')
  const [pendingEnd, setPendingEnd] = useState('')
  const datePopupRef = useRef<HTMLDivElement | null>(null)

  // Close popover on outside click
  useEffect(() => {
    if (!showDatePicker) return
    const handler = (e: MouseEvent) => {
      if (datePopupRef.current && !datePopupRef.current.contains(e.target as Node)) {
        setShowDatePicker(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showDatePicker])

  // Status endpoint URL — custom range OR preset.
  const statusUrl = customRange
    ? `/api/status?start=${customRange.start}&end=${customRange.end}`
    : `/api/status?days=${period}`

  const { data, isLoading, error } = useApi<StatusResp>(statusUrl)
  const anomQ = useApi<AnomaliesListResp>('/api/anomalies?limit=25')
  // Parallel fetch of tenant-specific anomalies; merged into the same feed.
  const tenantAnomQ = useApi<TenantAnomaliesResp>('/api/tenant-anomalies')

  // Merge: tenant anomalies first (more actionable), then global anomalies.
  // Filter hidden categories (experiment-* shown on ghost.html only).
  const mergedAnomalies: AnomalyRow[] = useMemo(() => {
    const tenant = (tenantAnomQ.data?.status === 'success' ? tenantAnomQ.data.anomalies : []) || []
    const global = (anomQ.data?.status === 'success' ? anomQ.data.anomalies : []) || []
    return [...tenant, ...global].filter((a) => !HIDDEN_CATEGORIES.has(a.category || ''))
  }, [tenantAnomQ.data, anomQ.data])

  // Anomaly date badge — prefer tenant date (more current).
  const anomalyDataDate = tenantAnomQ.data?.date || anomQ.data?.detected_at || anomQ.data?.anomaly_date
  const freshness = getFreshness(anomalyDataDate)

  const cards: { key: string; data: StatusCard | undefined }[] = data?.cards ? [
    { key: 'mer',    data: data.cards.mer },
    { key: 'cac',    data: data.cards.cac },
    { key: 'margin', data: data.cards.margin },
    { key: 'leads',  data: data.cards.leads },
    { key: 'kpi5',   data: data.cards.kpi5 },
  ] : []

  const grouped = useMemo(() => bucketize(mergedAnomalies), [mergedAnomalies])
  const isAnomLoading = anomQ.isLoading || tenantAnomQ.isLoading
  const hasAnyAnomalies = mergedAnomalies.length > 0

  // Pre-fill custom picker with today / 7 days ago whenever it opens.
  const openDatePicker = () => {
    if (!pendingStart || !pendingEnd) {
      const today = new Date()
      const weekAgo = new Date(today.getTime() - 7 * 86400000)
      const iso = (d: Date) => d.toISOString().slice(0, 10)
      setPendingStart(iso(weekAgo))
      setPendingEnd(iso(today))
    }
    setShowDatePicker((v) => !v)
  }

  const applyCustomRange = () => {
    if (!pendingStart || !pendingEnd) return
    if (pendingStart > pendingEnd) return  // basic validation
    setCustomRange({ start: pendingStart, end: pendingEnd })
    setShowDatePicker(false)
  }

  const clearCustomRange = (days: number) => {
    setCustomRange(null)
    setPeriod(days)
  }

  return (
    <div className="mc-wrap">
      {/* Period toolbar */}
      <div className="mc-toolbar">
        <div className="seg-control">
          {PERIODS.map((p) => (
            <button key={p.label}
                    className={`seg-btn ${!customRange && period === p.days ? 'active' : ''}`}
                    onClick={() => clearCustomRange(p.days)}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="date-wrapper" ref={datePopupRef}>
          <button className={`date-trigger ${customRange ? 'active' : ''}`} onClick={openDatePicker}>
            {customRange ? `${customRange.start} → ${customRange.end}` : 'Custom'}
          </button>
          {showDatePicker && (
            <div className="date-popup">
              <div className="date-row">
                <div className="date-col">
                  <span className="date-label">Start</span>
                  <input type="date" className="date-input" value={pendingStart}
                         onChange={(e) => setPendingStart(e.target.value)} />
                </div>
                <div className="date-col">
                  <span className="date-label">End</span>
                  <input type="date" className="date-input" value={pendingEnd}
                         onChange={(e) => setPendingEnd(e.target.value)} />
                </div>
              </div>
              <button className="apply-btn"
                      disabled={!pendingStart || !pendingEnd || pendingStart > pendingEnd}
                      onClick={applyCustomRange}>
                Apply
              </button>
            </div>
          )}
        </div>
        <span className="compare-select" onClick={() => setCompareMode(compareMode === 'previous' ? 'yoy' : 'previous')}>
          Vs: <strong style={{ color: 'var(--text-main)' }}>{compareMode === 'previous' ? 'Previous Period' : 'Last Year'}</strong>
        </span>
      </div>

      {/* Promo banner */}
      {data?.promo?.name && (
        <div className="promo-banner">
          {/* Tag SVG — no emoji */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M20.59 13.41 13.41 20.59a2 2 0 0 1-2.82 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <div>
            <div className="promo-name">{data.promo.name}</div>
            <div className="promo-dates">{data.promo.start_date} → {data.promo.end_date}</div>
          </div>
          {data.promo.tier && <span className="promo-tier">{data.promo.tier}</span>}
        </div>
      )}

      {/* Insights link */}
      <Link to="/reports" className="insights-link">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>9Fig Insights</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ask questions about your marketing data</div>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={2} style={{ marginLeft: 'auto' }}><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
      </Link>

      {/* Status cards */}
      <div className="status-grid">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="status-card">
              <div className="status-light" style={{ background: 'var(--text-muted)' }} />
              <div className="status-title">Loading…</div>
              <div className="status-val">—</div>
              <div className="status-sub">…</div>
            </div>
          ))
        ) : error ? (
          <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: 'var(--danger)' }}>
            Error: {(error as Error).message}
          </div>
        ) : data?.status === 'no_data' ? (
          <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            {data.message || 'Data not available for this tenant'}
          </div>
        ) : (
          (cards || []).map(({ key, data: c }) => (
            <div key={key} className={`status-card ${cardClass(c?.status)}`}>
              <div className="status-light" />
              <div className="status-title">{c?.label || key.toUpperCase()}</div>
              <div className="status-val">{cardValue(c)}</div>
              <div className="status-sub">{c?.subtitle || ''}</div>
            </div>
          ))
        )}
      </div>

      {/* Anomalies — merged tenant + global feed, grouped by category */}
      <div className="tracker-section">
        <div className="tracker-header">
          <div className="tracker-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Anomalies &amp; Signals
            {anomalyDataDate && (
              <span className={`freshness-badge ${freshness}`}>{formatDateBadge(anomalyDataDate)}</span>
            )}
            {data?.anomalies?.total != null && (
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', background: 'var(--bg-element)', padding: '3px 8px', borderRadius: 4 }}>
                {data.anomalies.total ?? 0} total · {data.anomalies.critical ?? 0} critical · {data.anomalies.high ?? 0} high
              </span>
            )}
          </div>
        </div>
        <table className="tracker-table">
          <tbody>
            {isAnomLoading && (
              <tr><td className="tracker-cell" colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading anomalies…</td></tr>
            )}
            {!isAnomLoading && !hasAnyAnomalies && (
              <tr><td className="tracker-cell" colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No anomalies in the current window. All systems healthy.</td></tr>
            )}
            {!isAnomLoading && hasAnyAnomalies && GROUP_RENDER_ORDER.map((groupKey) => {
              const items = grouped[groupKey] || []
              if (items.length === 0) return null
              const label = CATEGORY_GROUPS[groupKey]?.label || 'Other'
              return (
                <GroupRows key={groupKey} groupKey={String(groupKey)} label={label} items={items} />
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────── Sub-components ───────────────────

interface GroupRowsProps { groupKey: string; label: string; items: AnomalyRow[] }

function GroupRows({ groupKey, label, items }: GroupRowsProps) {
  return (
    <>
      <tr className="tracker-group-header">
        <td colSpan={4}>
          {label}<span className="group-count">({items.length})</span>
        </td>
      </tr>
      {(items || []).map((a, i) => (
        <AnomalyTableRow key={a.id || a.anomaly_id || `${groupKey}-${i}`} row={a} />
      ))}
    </>
  )
}

interface RowProps { row: AnomalyRow }

// One anomaly row. Renders tier chip, title/desc, channel, $/day impact OR date.
// Reuses lineage button pattern from vanilla. For investigate behavior, we link
// internally to /reports (matches vanilla `window.location.href = 'reports.html'`).
function AnomalyTableRow({ row }: RowProps) {
  const tier = (row.tier || 'medium').toLowerCase()
  const cat = row.category || ''
  const isAtRisk = AT_RISK_CATEGORIES.has(cat)
  const isNegative = NEGATIVE_IMPACT_CATEGORIES.has(cat)

  const rawImpact = row.daily_impact ?? 0
  const effectiveImpact = isNegative ? -Math.abs(rawImpact) : rawImpact
  const impactClass = isAtRisk
    ? 'impact-risk'
    : (effectiveImpact < 0 ? 'impact-bad' : 'impact-good')
  const impactPrefix = isAtRisk ? '' : (effectiveImpact >= 0 ? '+' : '-')
  const impactSuffix = isAtRisk ? ' at risk' : '/day'
  const hasImpact = typeof row.daily_impact === 'number'

  const dateStr = row.date || row.anomaly_date || ''
  const formattedDate = dateStr ? (parseDateSafe(dateStr)?.toLocaleDateString() ?? '') : ''

  return (
    <tr className="tracker-row">
      <td className="tracker-cell" style={{ width: 80 }}>
        <span className={`tier ${tierClass(tier)}`}>{tier}</span>
      </td>
      <td className="tracker-cell">
        <div className="title-cell">{row.title || row.category || 'Anomaly'}</div>
        {row.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{row.description}</div>}
        {row.action && <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>→ {row.action}</div>}
      </td>
      <td className="tracker-cell channel-cell" style={{ width: 140 }}>{row.channel || ''}</td>
      <td className="tracker-cell" style={{ width: 160, textAlign: 'right' }}>
        {hasImpact ? (
          <span className={`impact-val ${impactClass}`}>
            {impactPrefix}${Math.abs(rawImpact).toLocaleString()}{impactSuffix}
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formattedDate}</span>
        )}
      </td>
    </tr>
  )
}
